import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface FMRAuthGuardProps {
  children: React.ReactNode;
}

const SESSION_DURATION = 2 * 60 * 60 * 1000; // 2 hours
const WARNING_THRESHOLD = 10 * 60 * 1000; // Show warning 10 minutes before expiry

export default function FMRAuthGuard({ children }: FMRAuthGuardProps) {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    const checkAuth = () => {
      const session = localStorage.getItem('fmr_session');

      if (!session) {
        navigate('/fmr-portal/login');
        return;
      }

      try {
        const sessionData = JSON.parse(session);
        const now = Date.now();

        // Check if session expired
        if (sessionData.expiresAt && now >= sessionData.expiresAt) {
          localStorage.removeItem('fmr_session');
          navigate('/fmr-portal/login');
          return;
        }

        // Check if we should show warning
        const timeLeft = sessionData.expiresAt - now;
        if (timeLeft <= WARNING_THRESHOLD && timeLeft > 0) {
          setShowExpiryWarning(true);
          const minutes = Math.ceil(timeLeft / 60000);
          setTimeRemaining(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
        }

        setIsChecking(false);
      } catch (e) {
        localStorage.removeItem('fmr_session');
        navigate('/fmr-portal/login');
      }
    };

    checkAuth();

    // Check session every minute
    const interval = setInterval(checkAuth, 60000);
    return () => clearInterval(interval);
  }, [navigate]);

  const handleExtendSession = () => {
    const session = localStorage.getItem('fmr_session');
    if (session) {
      try {
        const sessionData = JSON.parse(session);
        const now = Date.now();
        sessionData.expiresAt = now + SESSION_DURATION;
        sessionData.loginTime = now;
        localStorage.setItem('fmr_session', JSON.stringify(sessionData));
        setShowExpiryWarning(false);
      } catch (e) {
        // Handle error silently
      }
    }
  };

  if (isChecking) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-2xl">
            <i className="ri-shield-check-line text-3xl text-white"></i>
          </div>
          <i className="ri-loader-4-line text-3xl text-amber-500 animate-spin"></i>
          <p className="text-slate-400 mt-4 text-sm">Verifying access...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      
      {/* Session Expiry Warning Banner */}
      {showExpiryWarning && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] max-w-md w-full mx-4">
          <div className="bg-amber-500 text-white rounded-lg shadow-2xl p-4 flex items-center gap-3 border border-amber-400">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-time-line text-xl"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm mb-0.5">Session Expiring Soon</p>
              <p className="text-xs text-white/90">Your session will expire in {timeRemaining}</p>
            </div>
            <button
              onClick={handleExtendSession}
              className="px-4 py-2 bg-white text-amber-600 rounded-lg font-semibold text-sm hover:bg-amber-50 transition-colors whitespace-nowrap flex-shrink-0"
            >
              Extend
            </button>
          </div>
        </div>
      )}
    </>
  );
}